import { isInvalidISODate } from "src/helpers/dateHelper";
import {
  isInvalidAsn,
  isInvalidProductId,
  isInvalidProductName,
  isInvalidQuantity,
  isInvalidString,
  isInvalidFileName
} from "src/validation/stringValidation";
import { isInvalidFileSize } from "src/helpers/fileHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import {
  OrderProductCheckInInputField,
  NonOrderProductCheckInInputField,
  CheckInPurchaseOrderFormDataKey
} from "src/types";

class CheckInPurchaseOrderFormValidator extends FormValidator {
  private static validateAsn(value: unknown): ValidationError<"asn"> | undefined {
    if (!value) return undefined;
    return isInvalidAsn(value) ? new ValidationError("Enter a valid ASN or PO ID", "asn") : undefined;
  }

  private static validateCheckInImages(value: unknown): ValidationError<"checkInImages"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    if (value.length > 10) {
      return new ValidationError("Can only upload up to 10 files", "checkInImages");
    }

    for (const arrayValue of value as File[]) {
      if (!arrayValue) {
        return new ValidationError("Upload an image file", "checkInImages");
      }

      if (isInvalidFileSize(arrayValue)) {
        return new ValidationError("A file is too large", "checkInImages");
      }

      if (isInvalidFileName(arrayValue.name)) {
        return new ValidationError("File name is invalid", "checkInImages");
      }
    }

    return undefined;
  }

  private static validateOrderProductCheckIns(value: unknown): ValidationError<"orderProductCheckIns"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return new ValidationError("Invalid order product check-ins", "orderProductCheckIns");
    }

    for (const arrayValue of value) {
      const { productId, receivedQty, receivedUkQty, receivedEuQty, hasWarehouseSplit, expiryDates } =
        arrayValue as OrderProductCheckInInputField;

      if (expiryDates.some(({ date }) => date && isInvalidISODate(date))) {
        return new ValidationError(
          `(${productId}) Order product check-in has invalid date (example format: '01/01/2026')`,
          "orderProductCheckIns"
        );
      }

      if (expiryDates.some(({ qty }) => qty && isInvalidQuantity(qty, 1, 100000))) {
        return new ValidationError(
          `(${productId}) Order product check-in has invalid quantity for the given expiry date`,
          "orderProductCheckIns"
        );
      }

      if (hasWarehouseSplit) {
        if (isInvalidQuantity(receivedUkQty?.toString() ?? "", 0, 100000)) {
          return new ValidationError(
            `(${productId}) Order product check-in has invalid UK quantity`,
            "orderProductCheckIns"
          );
        }
        if (isInvalidQuantity(receivedEuQty?.toString() ?? "", 0, 100000)) {
          return new ValidationError(
            `(${productId}) Order product check-in has invalid EU quantity`,
            "orderProductCheckIns"
          );
        }
      } else if (isInvalidQuantity(receivedQty.toString(), 0, 100000)) {
        return new ValidationError(
          `(${productId}) Order product check-in has invalid quantity`,
          "orderProductCheckIns"
        );
      }
    }

    return undefined;
  }

  private static validateNonOrderProductCheckIns(
    value: unknown
  ): ValidationError<"nonOrderProductCheckIns"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    for (const arrayValue of value) {
      const { productId, productName, receivedQty, receivedUkQty, receivedEuQty, hasWarehouseSplit, expiryDates } =
        arrayValue as NonOrderProductCheckInInputField;

      if (productId && isInvalidProductId(productId)) {
        return new ValidationError(
          `${productId ? `(${productId})` : ""} Product check-in has invalid product ID`,
          "nonOrderProductCheckIns"
        );
      }

      if (expiryDates.some(({ date }) => date && isInvalidISODate(date))) {
        return new ValidationError(
          `(${productId}) Product check-in has invalid date (example format: '01/01/2026')`,
          "nonOrderProductCheckIns"
        );
      }

      if (expiryDates.some(({ qty }) => qty && isInvalidQuantity(qty, 1, 100000))) {
        return new ValidationError(
          `(${productId}) Product check-in has invalid quantity for the given expiry date`,
          "nonOrderProductCheckIns"
        );
      }

      if (hasWarehouseSplit) {
        if (isInvalidQuantity(receivedUkQty?.toString() ?? "", 0, 100000)) {
          return new ValidationError(
            `(${productId}) Product check-in has invalid UK quantity`,
            "nonOrderProductCheckIns"
          );
        }
        if (isInvalidQuantity(receivedEuQty?.toString() ?? "", 0, 100000)) {
          return new ValidationError(
            `(${productId}) Product check-in has invalid EU quantity`,
            "nonOrderProductCheckIns"
          );
        }
        const total = (Number(receivedUkQty) || 0) + (Number(receivedEuQty) || 0);
        if (total < 1) {
          return new ValidationError(
            `(${productId}) Product check-in total quantity must be at least 1`,
            "nonOrderProductCheckIns"
          );
        }
      } else if (isInvalidQuantity(receivedQty, 1, 100000)) {
        return new ValidationError(`(${productId}) Product check-in has invalid quantity`, "nonOrderProductCheckIns");
      }

      if (isInvalidProductName(productName)) {
        return new ValidationError(
          `(${productId}) Product check-in has invalid product name`,
          "nonOrderProductCheckIns"
        );
      }
    }

    return undefined;
  }

  private static validateDefects(value: unknown): ValidationError<"defects"> | undefined {
    return value && isInvalidString(value, 2, 800)
      ? new ValidationError("Defects is too long or invalid", "defects")
      : undefined;
  }

  private static readonly validationStrategies: Record<
    CheckInPurchaseOrderFormDataKey,
    (value: unknown) => ValidationError<CheckInPurchaseOrderFormDataKey> | undefined
  > = {
    asn: CheckInPurchaseOrderFormValidator.validateAsn,
    checkInImages: CheckInPurchaseOrderFormValidator.validateCheckInImages,
    orderProductCheckIns: CheckInPurchaseOrderFormValidator.validateOrderProductCheckIns,
    nonOrderProductCheckIns: CheckInPurchaseOrderFormValidator.validateNonOrderProductCheckIns,
    defects: CheckInPurchaseOrderFormValidator.validateDefects
  };

  static validateWarehouseCheckInFormData<T extends CheckInPurchaseOrderFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      CheckInPurchaseOrderFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { CheckInPurchaseOrderFormValidator };
